/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import {
  Directive, ElementRef, forwardRef, HostListener, Input, Renderer2,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Implements ControlValueAccessor for elements with contenteditable.
 */
@Directive({
  selector: '[contenteditable][formControlName],'
        + '[contenteditable][formControl],'
        + '[contenteditable][ngModel]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContenteditableValueAccessorDirective),
      multi: true,
    },
  ],
  standalone: false,
})
export class ContenteditableValueAccessorDirective implements ControlValueAccessor {

  @Input() disabled: boolean = false;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
  ) { }

  private onTouched = () => {};

  private onChange: (value: string) => void = () => { };

  registerOnChange(onChange: (value: string) => void) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }

  @HostListener('input')
  onInput(): void {
    // Browsers will place a <br> tag when contenteditable is empty.
    // We want to remove it to keep the model in sync with the view.
    if (this.elementRef.nativeElement.innerHTML === '<br>') {
      this.elementRef.nativeElement.innerHTML = '';
    }
    this.onChange(this.elementRef.nativeElement.innerText);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  setDisabledState(disabled: boolean) {
    this.renderer.setAttribute(
      this.elementRef.nativeElement,
      'contenteditable',
      String(!disabled),
    );
  }

  writeValue(value: string) {
    this.renderer.setProperty(
      this.elementRef.nativeElement,
      'innerText',
      value,
    );
  }
}
